import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { isEmpty } from 'lodash';
import config from '@root/src/config';
import api from '@root/src/services/api';
import { ENGAGED_LIST_SEARCH_FILTER_KEYS, EVENT_LIST_SEARCH_FILTER } from '@root/src/types';
import useLazyLoading, { LazyLoader } from '@root/src/hooks/useLazyLoading';
import {
  pushEngagedPaginated,
  selectEventCategories,
  selectEventEngagedCantonsOptions,
  selectEventEngagedCategoriesLoaded,
  selectEventAthletesCountForEvent,
  selectEventEngagedFilter,
  selectEventEngagedNationalitiesOptions,
  setEngagedFilter,
} from '@features/eventDetail/slices/eventDetailServerSlice';
import EngagedCategoryList from '@features/eventDetail/components/EngagedCategoryList';
import EventListPageTemplate from '@features/eventDetail/templates/EventListPageTemplate';
import { getQueryEventId } from '@features/eventDetail/utils';
import { SEARCH_TYPES, searchIsValid } from '@features/ui/components/SearchInput';

const EngagedList = () => {
  const router = useRouter();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const eventId = getQueryEventId(router.query);

  const categories = useSelector(selectEventCategories(eventId));
  const categoriesLoaded = useSelector(selectEventEngagedCategoriesLoaded(eventId));
  const engagedCount = useSelector(selectEventAthletesCountForEvent(eventId));
  const engagedCantonsOptions = useSelector(selectEventEngagedCantonsOptions(eventId));
  const engagedNationalitiesOptions = useSelector(selectEventEngagedNationalitiesOptions(eventId));
  const engagedFilter = useSelector(selectEventEngagedFilter(eventId));

  const [categoryFilter, setCategoryFilter] = useState(-1);

  const searchOptions = getSearchOptions(t, engagedCantonsOptions, engagedNationalitiesOptions);
  const isDowngradedMode = eventId >= config.customHandlings.euroCategoriesIds;

  const processData = (json: any, overrideCurrent?: boolean) => {
    dispatch(pushEngagedPaginated({
      engagedApi: json,
      eventId,
      overrideCurrent,
    }));
  };
  const params = {
    eventId,
    categoryId: categoryFilter === -1 ? null : categoryFilter,
    filterBy: engagedFilter.query === '' ? {} : getEngagedFilterForAPI(engagedFilter),
  };
  const fetchIsValid = searchIsValid(
    searchOptions.find(option => option.value === engagedFilter.filterBy)?.type,
    engagedFilter.query,
  ) && !isDowngradedMode;
  const { observerLoadMoreRef, isLoading, hasMore } = useLazyLoading({
    endpoint: api.results.engaged.events.list,
    params,
    processData,
    checkIfHasNoMoreData,
    resetListDependencies: useMemo(() => ([categoryFilter, engagedFilter]), [categoryFilter, engagedFilter]),
    fetchIsValid,
  });

  const groupOptions = [
    {
      label: t('engagedSearchInput_selectAllOptionsLabel.message'),
      value: -1,
    }, ...categories.map(category => ({
      label: category.name,
      value: category._idMso,
    })),
  ];

  return (
    <EventListPageTemplate
      pageTitle={t('engagedList_headerTitle.message')}
      pageCount={!isDowngradedMode && t('engagedList_headerNbPersons.message', { count: engagedCount })}
      groupOptions={groupOptions}
      onGroupChange={(e) => setCategoryFilter(e as number)}
      searchOptions={searchOptions}
      onSearchSubmit={(query, filterBy) => dispatch(setEngagedFilter({
        query,
        filterBy,
        eventId,
      }))}
      isLoading={isLoading}
      disableSearch={isDowngradedMode}
      disableFilter={isDowngradedMode}
      filter={engagedFilter}
    >
      {categoriesLoaded.map(category => (
        <EngagedCategoryList
          categoryId={category}
          key={category}
          isLoading={isLoading}
        />
      ))}

      <LazyLoader isLoading={isLoading} hasMore={hasMore} ref={observerLoadMoreRef} />
    </EventListPageTemplate>
  );
};

export default EngagedList;

const getSearchOptions = (t, engagedCantonsOptions, engagedNationalitiesOptions) => [
  {
    label: t('eventList_searchOptionAthlete.message'),
    value: EVENT_LIST_SEARCH_FILTER.ATHLETE,
    type: SEARCH_TYPES.TEXT,
  },
  {
    label: t('eventList_searchOptionState.message'),
    value: EVENT_LIST_SEARCH_FILTER.DEPARTEMENT,
    type: SEARCH_TYPES.SELECT,
    options: engagedCantonsOptions,
  },
  {
    label: t('eventList_searchOptionYear.message'),
    value: EVENT_LIST_SEARCH_FILTER.YEAR,
    type: SEARCH_TYPES.BIRTH_YEAR,
  },
  {
    label: t('eventList_searchOptionLocality.message'),
    value: EVENT_LIST_SEARCH_FILTER.LOCALITY,
    type: SEARCH_TYPES.TEXT,
  },
  {
    label: t('eventList_searchOptionNationality.message'),
    value: EVENT_LIST_SEARCH_FILTER.NATIONALITY,
    type: SEARCH_TYPES.SELECT,
    options: engagedNationalitiesOptions,
  },
  {
    label: t('eventList_searchOptionTeam.message'),
    value: EVENT_LIST_SEARCH_FILTER.TEAM,
    type: SEARCH_TYPES.TEXT,
  },
];

const searchFilterOptionToAPIKey = {
  [EVENT_LIST_SEARCH_FILTER.ATHLETE]: ENGAGED_LIST_SEARCH_FILTER_KEYS.athlete,
  [EVENT_LIST_SEARCH_FILTER.DEPARTEMENT]: ENGAGED_LIST_SEARCH_FILTER_KEYS.canton,
  [EVENT_LIST_SEARCH_FILTER.YEAR]: ENGAGED_LIST_SEARCH_FILTER_KEYS.year,
  [EVENT_LIST_SEARCH_FILTER.LOCALITY]: ENGAGED_LIST_SEARCH_FILTER_KEYS.location,
  [EVENT_LIST_SEARCH_FILTER.NATIONALITY]: ENGAGED_LIST_SEARCH_FILTER_KEYS.nationality,
  [EVENT_LIST_SEARCH_FILTER.TEAM]: ENGAGED_LIST_SEARCH_FILTER_KEYS.teamname,
};

const getEngagedFilterForAPI = (engagedFilter) => {
  const filterByAPI = searchFilterOptionToAPIKey[engagedFilter.filterBy];
  return { [filterByAPI]: engagedFilter.query };
};

export const checkIfHasNoMoreData = (json: any) => isEmpty(json);
